<template>
    <div class="button green px-8px" @click="validateSheet">
        <span class="material-icons valign-middle text-23px">rule</span>
    </div>
</template>

<script>
import { getSheetsData } from '@/api/gSheets'
import { listCoinIds } from '@/api/coingecko'
import { isValid, parse, getYear } from 'date-fns'
import { parseStringifiedNumbersFromSheetData } from '@/services/transformers/numbers'

export default {
    props:{
        spreadsheets: Array, // [{key, tab}]
    },
    data(){
        return{
            processing: false,
        }
    },
    computed:{
        isCombinedPortfolio(){
            return this.spreadsheets.length > 1
        },
    },
    methods:{
        async validateSheet(){
            if(this.processing){
                return
            }
            this.processing = true

            const geckoCoinIds = await listCoinIds()

            const geckoIdToSymbolMap = geckoCoinIds.reduce((mapping, row)=>{
                mapping[row.id] = row.symbol
                return mapping
            }, {})

            const sheetData = await this.getSpreadsheetData()

            for(let i = 0; i < sheetData.length; i++){
                const row = sheetData[i]
                try {
                    this.assertValidRow(row, geckoIdToSymbolMap)
                } catch (error) {
                    alert(`Invalid entry detected on sheet '${row.sheetTab}' row ${row.rowNr}: ${error.message}`)
                    this.processing = false
                    return
                }
            }

            try {
                this.assertNoInvalidDateTimePerAssetCombo(sheetData)
            } catch (error) {
                alert(error.message)
                this.processing = false
                return
            }

            alert(`Success: No anomalies detected in the given spreadsheet(s) ✓`)

            this.processing = false
        },
        async getSpreadsheetData(){
            let allSheetData = []
            for(const {key, tab} of this.spreadsheets){
                let sheetData = await getSheetsData(key, {date: 'Date', time: 'Time', transaction_type: 'Transaction type', asset: 'Asset', quantity: 'Quantity', total_price: 'Total', currency: 'Currency', gecko_id: 'Coingecko id'}, tab)
                sheetData = parseStringifiedNumbersFromSheetData(sheetData)

                // add row numbers
                for(let i = 0; i < sheetData.length; i++){
                    sheetData[i].rowNr = i+2
                    sheetData[i].sheetTab = tab
                }

                allSheetData = [...allSheetData, ...sheetData]
            }
            return allSheetData
        },
        assertValidRow(row, geckoIdToSymbolMap){
            // check if given asset matches given gecko id (e.g. asset: USDT -> coingecko id: thether)
            if(geckoIdToSymbolMap[row.gecko_id].toUpperCase() !== row.asset.toUpperCase()){
                throw new Error(`Coingecko id '${row.gecko_id}' doesn't match asset '${row.asset}'.`)
            }
            // check if transaction type is filled correctly
            if(row.transaction_type.toUpperCase() !== 'BUY' && row.transaction_type.toUpperCase() !== 'SELL'){
                throw new Error("Transaction type should either be 'Buy' or 'Sell'.")
            }
            // some date checks
            const parsedDate = parse(row.date, 'dd/MM/yyyy', new Date())
            if(!isValid(parsedDate)){
                throw new Error('Invalid date given. Format should be DD/MM/YYYY.')
            }
            if(getYear(parsedDate) < 2010){
                throw new Error('Date falls before 2010, most likely a typo.')
            }
            // number checks
            if(!this.isNumericString(row.quantity) || row.quantity < 0){
                throw new Error('Quantity cannot be less than zero.')
            }
            if(!this.isNumericString(row.total_price) || row.total_price < 0){
                throw new Error('Total cannot be less than zero.')
            }
            if(row.time == null){
                throw new Error('Time field is null, should not happen.')
            }
            if(row.time != ''){
                if(!this.isNumericString(row.time) || row.time < 0){
                    throw new Error('Time should either be a number (not less than zero) or empty (avoid whitespaces).')
                }
            }
        },
        isNumericString(value){
            if(typeof value === 'string'){
                if(value.trim() == ''){
                    return false
                }
            }
            return !isNaN(value)
        },
        assertNoInvalidDateTimePerAssetCombo(sheetData){
            const uniqueDateCombos = []
            const uniqueDateTimeCombos = []
            for(let i = 0; i < sheetData.length; i++){
                const row = sheetData[i]
                const dateKey = `${row.gecko_id}_${row.date}`
                const dateTimeKey = `${row.gecko_id}_${row.date}_${row.time}`
                if(uniqueDateTimeCombos.includes(dateTimeKey)){
                    throw new Error(`Invalid entry detected on sheet '${row.sheetTab}' row ${row.rowNr}: Duplicate date time found for this asset - Add a 'Time' column to add the order of transactions.`)
                }
                if(uniqueDateCombos.includes(dateKey)){
                    const dateTimeKeyWithEmptyTime = `${dateKey}_`
                    if(uniqueDateTimeCombos.includes(dateTimeKeyWithEmptyTime) || dateTimeKey == dateTimeKeyWithEmptyTime){
                        // this means we already have an order for this asset on the given date
                        // yet we found a date without a given time -> which probably means this was accidentally left empty
                        throw new Error(`Invalid entry detected on sheet '${row.sheetTab}' row ${row.rowNr}: Duplicate date found for this asset with empty time field`)
                    }
                }
                uniqueDateCombos.push(dateKey)
                uniqueDateTimeCombos.push(dateTimeKey)
            }
        },
    }
}
</script>